import {Component} from '@angular/core';
import {Role} from '../../../model/auth/Role';
import {TenantService} from '../../../service/tenant.service';
import {ExamCardStats, localizeUserExamGrade, UserExamGrade, UserExamStats} from '../../../model/stats/Stats';
import {NotificationHandler} from '../../../helper/notification.handler';
import {StatsService} from '../../../service/stats.service';
import {ActivatedRoute} from '@angular/router';
import {MobileAwareComponent} from '../../../common/mobile-aware/mobile-aware.component';
import {BreakpointObserver} from '@angular/cdk/layout';
import {ContestTaskStats, ContestTaskType, localizeContestTaskType} from '../../../model/contest/ContestTask';
import {ContestEvent, ContestEventType, QUESTION_INDEX_COMPARATOR} from '../../../model/contest/ContestEvent';
import {ContestTaskService} from '../../../service/contest.task.service';
import {UserService} from '../../../service/user.service';
import {User} from '../../../model/auth/User';
import {CertificateService} from '../../../service/certificate.service';
import {ContestService} from '../../../service/contest.service';
import {Contest} from '../../../model/contest/Contest';
import {DataHelper} from '../../../helper/data.helper';

@Component({
  selector: 'app-exam-card-stats',
  templateUrl: './exam-card-stats.component.html',
  styleUrls: ['./exam-card-stats.component.scss']
})
export class ExamCardStatsComponent extends MobileAwareComponent {

  private readonly ALPHABET = $localize`:General|Alphabet indexes@@general-alphabet-indexes:ABCDEFGHIJKLMNOPQRSTUVWXYZ`;

  readonly examGrades = UserExamGrade;
  readonly roles = [Role.Host, Role.Coordinator, Role.Teacher, Role.Student];

  competitionId?: string;
  contestId?: string;
  userId?: string;

  contest?: Contest;

  inProgress = false;
  certificateNotConfigured = false;

  certificateGetInProgress = false;
  certificateUrl?: string;

  user?: User;

  stats?: ExamCardStats;
  taskStats?: ContestTaskStats;

  today!: Date;

  answers = new Map<number, ContestEvent[]>();

  constructor(private statsService: StatsService,
              private contestTaskService: ContestTaskService,
              private certificateService: CertificateService,
              private contestService: ContestService,
              private tenantService: TenantService,
              private userService: UserService,
              private route: ActivatedRoute,
              breakpointObserver: BreakpointObserver) {
    super(breakpointObserver);
  }

  initContent() {
    this.inProgress = true;

    this.today = new Date();

    const tenant = this.tenantService.getTenant();
    if (tenant && tenant.id) {
      this.competitionId = tenant.id;
    }

    this.route.params.subscribe(params => {
      if (params) {
        if (params.userId) this.userId = params.userId as string;
        if (params.contestId) this.contestId = params.contestId as string;

        this.loadUser();
      }
    });
  }

  private loadUser() {
    if (!this.userId) return;

    this.userService.getUserDetails(this.userId).subscribe(user => {
      this.user = user;

      this.loadContest();
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|User details fetch has failed message@@notifications-user-details-fetch-failed-error:User details fetch has failed`, error);

      this.inProgress = false;
    });
  }

  private loadContest() {
    if (!this.contestId) return;

    this.contestService.getDetails(this.contestId, true).subscribe(contest => {
      this.contest = contest;

      this.loadExamCardStats();
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Contest details fetch has failed message@@notifications-contest-details-fetch-failed-error:Contest details fetch has failed`, error);
      this.inProgress = false;
    });
  }

  loadExamCardStats() {
    if (!this.competitionId || !this.contestId || !this.user || !this.user.region || !this.user.schoolId) return;

    this.inProgress = true;

    this.stats = undefined;

    this.statsService.getUserExamCardStats(this.contestId, this.competitionId, this.user.region, this.user.schoolId, this.user.id)
      .subscribe(stats => {
        this.stats = stats;

        if (this.stats.events) {
          this.stats.events
            .filter(event => event.eventType == ContestEventType.answer)
            .forEach(event => {
              if (event.sectionIndex) {
                if (!this.answers.has(event.sectionIndex)) this.answers.set(event.sectionIndex, []);
                const sectionAnswers = this.answers.get(event.sectionIndex) as ContestEvent[];
                sectionAnswers.push(event);
              }
            });
          this.answers.forEach(answers => {
            answers.sort(QUESTION_INDEX_COMPARATOR);
          });
        }

        if (this.contestId && this.stats.exam && this.stats.exam.taskId && this.stats.exam.taskType) {
          this.loadTaskStats(this.contestId, this.stats.exam.taskId, this.stats.exam.taskType);
        } else {
          this.inProgress = false;
        }
      }, error => {
        NotificationHandler.handleError($localize`:Notifications|Stats fetch has failed message@@notifications-stats-fetch-failed-error:Statistical data fetch has failed`, error);
      });
  }

  private loadTaskStats(contestId: string, taskId: string, type: ContestTaskType) {
    this.inProgress = true;

    this.taskStats = undefined;
    this.contestTaskService.getContestTaskStats(contestId, taskId, type).subscribe(task => {
      this.taskStats = task;
      this.taskStats.section_indexes.sort((s1, s2) => s1 - s2);

      this.inProgress = false;
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Contest task details fetch has failed message@@notifications-contest-task-details-fetch-failed-error:Contest task details fetch has failed`, error);
      this.inProgress = false;
    });
  }

  localizeExamGrade(exam?: UserExamStats) {
    return exam && exam.grade
      ? localizeUserExamGrade(exam.grade)
      : this.stats && !this.stats.participated && this.contest && this.contest.endsAt && this.contest.endsAt < new Date()
        ? localizeUserExamGrade(UserExamGrade.average)
        : '';
  }

  localizeTaskType(taskType?: ContestTaskType) {
    return taskType ? localizeContestTaskType(taskType) : '';
  }

  private static sectionSubtotal(answers?: ContestEvent[]) {
    return answers
      ? answers.map(answer => answer.points || 0).reduce((a1, a2) => a1 + a2)
      : 0;
  }

  getTotalPointsCalculation(exam: UserExamStats): string {
    let sectionPoints = '';
    let firstAdded = false;
    let firstSectionSign = '+';
    if (this.taskStats) {
      this.taskStats.section_indexes.forEach(sectionIndex => {
        const sectionSubtotal = ExamCardStatsComponent.sectionSubtotal(this.answers.get(sectionIndex));
        sectionPoints += `${firstAdded ? sectionSubtotal >= 0 ? ' + ' : ' - ' : ''}${Math.abs(sectionSubtotal).toFixed(2)}`;
        if (!firstAdded) {
          firstAdded = true;
          if (sectionSubtotal < 0) firstSectionSign = '-';
        }
      });
    }

    return `${exam.initPoints ? `${firstSectionSign} ` : ''}${sectionPoints}`;
  }

  getBonusPointsCalculation(bonusPoints: number) {
    return `${bonusPoints >= 0 ? '+' : '-'} ${bonusPoints}`;
  }

  getAnswerLabel(answerIndex: number): string {
    return answerIndex > 0 && answerIndex <= this.ALPHABET.length ? this.ALPHABET[answerIndex - 1] : '?';
  }

  requestCertificate(userId: string) {
    if (!this.contestId || this.certificateNotConfigured) return;

    this.certificateGetInProgress = true;
    this.certificateUrl = undefined;

    this.certificateService.requestCertificate(this.contestId, userId).subscribe(certificate => {
      if (certificate.url) {
        this.certificateUrl = certificate.url;
        DataHelper.downloadFile(certificate.url);
      } else {
        this.certificateNotConfigured = true;
        NotificationHandler.handleWarning($localize`:Notifications|Contest certificates are not configured for contest message@@notifications-contest-certificate-not-configured-for-contest-error:Contest certificates are not configured for this contest by a host`);
      }

      this.certificateGetInProgress = false;
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Contest certificate request has failed message@@notifications-contest-certificate-request-failed-error:Contest certificate request has failed`, error);
      this.certificateGetInProgress = false;
    });
  }
}
